import { apiClient } from "./client";
import type {
  BackupCodesResponse,
  ChangePasswordRequest,
  ImpersonatorRead,
  LoginRequest,
  LoginResponse,
  RefreshRequest,
  Setup2FAResponse,
  SetupPasswordRequest,
  SetupPasswordResponse,
  TokenResponse,
  UpdateMeRequest,
  User,
  Verify2FASetupResponse,
} from "./types";

export async function login(payload: LoginRequest): Promise<LoginResponse> {
  const res = await apiClient.post<LoginResponse>("/auth/login", payload);
  return res.data;
}

export async function setup2FA(): Promise<Setup2FAResponse> {
  const res = await apiClient.post<Setup2FAResponse>("/auth/2fa/setup");
  return res.data;
}

export async function verifySetup2FA(
  code: string,
): Promise<Verify2FASetupResponse> {
  const res = await apiClient.post<Verify2FASetupResponse>(
    "/auth/2fa/verify-setup",
    { code },
  );
  return res.data;
}

export async function verifyLogin2FA(
  tempToken: string,
  code: string,
): Promise<TokenResponse> {
  const res = await apiClient.post<TokenResponse>("/auth/2fa/verify-login", {
    temp_token: tempToken,
    code,
  });
  return res.data;
}

export async function disable2FA(code: string): Promise<void> {
  await apiClient.post("/auth/2fa/disable", { code });
}

export async function regenerateBackupCodes(
  code: string,
): Promise<BackupCodesResponse> {
  const res = await apiClient.post<BackupCodesResponse>(
    "/auth/2fa/regenerate-backup-codes",
    { code },
  );
  return res.data;
}

export async function refresh(payload: RefreshRequest): Promise<TokenResponse> {
  const res = await apiClient.post<TokenResponse>("/auth/refresh", payload);
  return res.data;
}

export async function getMe(): Promise<User> {
  const res = await apiClient.get<User>("/auth/me");
  return res.data;
}

export async function updateMe(payload: UpdateMeRequest): Promise<User> {
  const res = await apiClient.patch<User>("/auth/me", payload);
  return res.data;
}

export async function changePassword(
  payload: ChangePasswordRequest,
): Promise<void> {
  await apiClient.post("/auth/change-password", payload);
}

export async function setupPassword(
  payload: SetupPasswordRequest,
): Promise<SetupPasswordResponse> {
  const res = await apiClient.post<SetupPasswordResponse>(
    "/auth/setup-password",
    payload,
  );
  return res.data;
}

export async function promoteSuperadmin(userId: string): Promise<User> {
  const res = await apiClient.post<User>("/auth/promote-superadmin", {
    user_id: userId,
  });
  return res.data;
}

export async function demoteSuperadmin(userId: string): Promise<User> {
  const res = await apiClient.post<User>("/auth/demote-superadmin", {
    user_id: userId,
  });
  return res.data;
}

export interface UserListParams {
  q?: string;
  page?: number;
  page_size?: number;
}

export interface UserListResponse {
  items: User[];
  total: number;
  page: number;
  page_size: number;
}

export interface ImpersonateResponse {
  access_token: string;
  refresh_token: string;
  token_type?: string;
  impersonating: ImpersonatorRead;
}

export async function impersonate(
  userId: string,
): Promise<ImpersonateResponse> {
  const res = await apiClient.post<ImpersonateResponse>("/auth/impersonate", {
    user_id: userId,
  });
  return res.data;
}

export async function stopImpersonation(): Promise<TokenResponse> {
  const res = await apiClient.post<TokenResponse>(
    "/auth/stop-impersonation",
  );
  return res.data;
}

export async function listUsers(
  params: UserListParams = {},
): Promise<UserListResponse> {
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (typeof value === "string" && value.trim() === "") continue;
    cleaned[key] = value;
  }
  const res = await apiClient.get<UserListResponse>("/auth/users", {
    params: cleaned,
  });
  return res.data;
}
